// SPDX-License-Identifier: Elastic-2.0
/**
 * UpdateBanner — Mirlai-branded system update notification (F-115 / T-014).
 *
 * Renders a dismissable info banner when a new Mirlai version is available.
 * Per ADR-003 Amendment §3, the underlying infrastructure (HA CDN, GitHub
 * Releases) must never appear in the UI — only the `mirlai` version string
 * from the unified manifest is surfaced.
 *
 * Behaviour:
 *   - Polls `GET /system/updates` via `useSystemUpdates` (once per hour).
 *   - When `available=true`: shows a banner with the new version + "Update"
 *     and "Later" buttons.
 *   - "Update" → calls POST /system/updates/apply, shows spinner while
 *     applying. Error state shows a Mirlai-branded message (no URLs/hosts).
 *   - "Later" → stores a dismissal in sessionStorage so it doesn't flash
 *     back on every route change within the same browser session.
 *   - Returns `null` when no update is available (zero DOM footprint).
 *   - `check_failed=true` → silent (no banner shown; update check was ambient).
 */
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSystemUpdates } from "./useSystemUpdates";
import styles from "./UpdateBanner.module.css";

const DISMISSED_KEY = "mirlai_update_dismissed";

function isDismissed(version: string): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === version;
  } catch {
    return false;
  }
}

function setDismissed(version: string): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // Non-critical — at worst the banner re-appears on next mount.
  }
}

export function UpdateBanner(): ReactElement | null {
  const { t } = useTranslation();
  const { status, isApplying, apply, applyError } = useSystemUpdates();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);

  if (!status?.available || status.check_failed) return null;

  const version = status.latest_version ?? "";

  if (localDismissed || isDismissed(version)) return null;

  function handleLater(): void {
    setDismissed(version);
    setLocalDismissed(true);
  }

  return (
    <div
      className={styles.banner}
      role="status"
      data-testid="system-update-banner"
    >
      <div className={styles.body}>
        <div className={styles.message}>
          {t("system_update.available_body", { version })}
        </div>

        {status.changelog && (
          <div className={styles.changelog}>
            <button
              type="button"
              className={styles.changelogToggle}
              onClick={() => setChangelogOpen((open) => !open)}
              aria-expanded={changelogOpen}
              data-testid="system-update-changelog-toggle"
            >
              {changelogOpen
                ? t("system_update.changelog_hide")
                : t("system_update.changelog_show")}
            </button>
            {changelogOpen && (
              <pre className={styles.changelogContent} data-testid="system-update-changelog">
                {status.changelog}
              </pre>
            )}
          </div>
        )}

        {applyError && (
          <div className={styles.error} data-testid="system-update-error">
            {t("system_update.apply_error")}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.laterBtn}
          onClick={handleLater}
          disabled={isApplying}
          data-testid="system-update-later"
        >
          {t("system_update.later_btn")}
        </button>
        <button
          type="button"
          className={styles.updateBtn}
          onClick={apply}
          disabled={isApplying}
          data-testid="system-update-apply"
        >
          {isApplying ? t("system_update.applying") : t("system_update.update_btn")}
        </button>
      </div>
    </div>
  );
}

export default UpdateBanner;
